import { defineStore } from "pinia";
import http from "@/http";
import { debounce } from "lodash-es";

export const useOrdersStore = defineStore("orders", {
  state: () => ({
    orders: [],
    loading: false,
    error: null,
    filters: {
      search: "",
      clients: [],
      warehouses: [],
      statuses: [],
      problems: null, // null, 0, or 1
      online: null, // null, 0, or 1
    },
    // Данные для фильтров
    availableClients: [],
    availableWarehouses: [],
    availableStatuses: [
      "all",
      "planned",
      "driving_to_load",
      "started_loading",
      "finished_loading",
      "is_delivered",
    ],
    routeData: {},
    abortController: null,
    reqId: 0,
  }),

  getters: {
    filteredOrders: (state) => {
      return state.orders;
    },

    ordersCount: (state) => {
      return state.orders.length;
    },

    ordersByStatus: (state) => {
      return (status) =>
        state.orders.filter((order) => order.status === status);
    },

    problemOrders: (state) => {
      return state.orders.filter((order) => order.hasProblem);
    },

    onlineOrders: (state) => {
      return state.orders.filter((order) => order.isOnline);
    },

    offlineOrders: (state) => {
      return state.orders.filter((order) => !order.isOnline);
    },

    ordersWithRoute: (state) => {
      return state.orders.map(o => ({
        ...o,
        ...state.routeData[o.name]
      }));
    },
  },

  actions: {
    debouncedFetchOrders: debounce(async function () {
      await this.fetchOrders();
    }, 300),

    async fetchOrders() {
      const params = this.buildQueryParams();

      // Отменяем предыдущий запрос
      if (this.abortController) {
        this.abortController.abort();
      }
      const controller = new AbortController();
      this.abortController = controller;

      const reqId = ++this.reqId;
      this.loading = true;
      this.error = null;

      try {
        const res = await http.get("/api/dispatcher/orders", {
          params,
          signal: controller.signal,
        });

        // Если это не самый свежий запрос — игнорируем ответ
        if (reqId !== this.reqId) return { status: "canceled" };

        if (res.status === 200 && res.data) {
          this.orders = res.data;
          return { status: "ok" };
        }
        return { status: "error" };
      } catch (err) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
          return { status: "canceled" };
        }
        this.error = err.message || "Ошибка при загрузке заказов";
        console.error("Error fetching orders:", err);
        return { status: "error" };
      } finally {
        if (reqId === this.reqId) {
          this.loading = false;
          this.abortController = null;
        }
      }
    },

    buildQueryParams() {
      const params = {};

      if (this.filters.search) {
        params.search = this.filters.search;
      }

      if (this.filters.clients.length > 0) {
        params["clients[]"] = this.filters.clients;
      }

      if (this.filters.warehouses.length > 0) {
        params["warehouses[]"] = this.filters.warehouses;
      }

      if (this.filters.statuses.length > 0) {
        params["statuses[]"] = this.filters.statuses;
      }

      if (this.filters.problems !== null) {
        params.problems = this.filters.problems;
      }

      if (this.filters.online !== null) {
        params.online = this.filters.online;
      }

      return params;
    },

    setSearchFilter(search) {
      this.filters.search = search;
    },

    setClientsFilter(clients) {
      this.filters.clients = clients;
    },

    setWarehousesFilter(warehouses) {
      this.filters.warehouses = warehouses;
    },

    setStatusesFilter(statuses) {
      this.filters.statuses = statuses;
    },

    setProblemsFilter(problems) {
      this.filters.problems = problems;
    },

    setOnlineFilter(online) {
      this.filters.online = online;
    },

    addClientFilter(clientId) {
      if (!this.filters.clients.includes(clientId)) {
        this.filters.clients.push(clientId);
      }
    },

    removeClientFilter(clientId) {
      this.filters.clients = this.filters.clients.filter(
        (id) => id !== clientId
      );
    },

    addWarehouseFilter(warehouseId) {
      if (!this.filters.warehouses.includes(warehouseId)) {
        this.filters.warehouses.push(warehouseId);
      }
    },

    removeWarehouseFilter(warehouseId) {
      this.filters.warehouses = this.filters.warehouses.filter(
        (id) => id !== warehouseId
      );
    },

    addStatusFilter(status) {
      if (!this.filters.statuses.includes(status)) {
        this.filters.statuses.push(status);
      }
    },

    removeStatusFilter(status) {
      this.filters.statuses = this.filters.statuses.filter((s) => s !== status);
    },

    clearFilters() {
      this.filters = {
        search: "",
        clients: [],
        warehouses: [],
        statuses: [],
        problems: null,
        online: null,
      };
    },

    clearAllFilters() {
      this.clearFilters();
      // this.fetchOrders();
    },

    async applyFilters() {
      await this.debouncedFetchOrders();
    },

    getOrderById(orderId) {
      return this.orders.find((order) => order.id === orderId);
    },

    getOrderByName(orderName) {
      return this.orders.find((order) => order.name === orderName);
    },

    resetOrders() {
      this.orders = [];
    },

    // Вспомогательные методы для работы с данными заказов
    formatLoadingTime(order) {
      return order.loadingTime || "-";
    },

    formatDeliveryTime(order) {
      return order.deliveryTime || "-";
    },

    getStatusText(status) {
      const statusMap = {
        all: "Все заказы",
        planned: "Запланирован",
        driving_to_load: "Ожидает погрузку",
        started_loading: "На погрузке",
        finished_loading: "В пути",
        is_delivered: "Доставлен",
      };
      return statusMap[status] || status;
    },

    setRouteData(orderName, data) {
      this.routeData[orderName] = data;
    },
  },
});
